import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  StarIcon, 
  MapPinIcon, 
  ClockIcon,
  CheckBadgeIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';
import defaultProviderImage from '../assets/images/provider.png';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Provider, Location } from '../types/api.types';
import { getProviderStatusInfo } from '../utils/providerStatus';
import { openDirections } from '../utils/navigation';
import SafeImage from './common/SafeImage';

interface ProviderCardProps {
  provider: Provider;
  onClick?: () => void;
  onBookClick?: (provider: Provider) => void;
  onClaimClick?: (provider: Provider) => void;
  scaleOnHover?: boolean;
  eagerLoad?: boolean;
}

const ProviderCard: React.FC<ProviderCardProps> = ({ provider, onClick, onBookClick, onClaimClick, scaleOnHover = false, eagerLoad = false }) => {
  const navigate = useNavigate();
  const isUnclaimed = provider.claimed === false;

  // Get location names if populated
  const locationNames = Array.isArray(provider.locations) && provider.locations.length > 0
    ? (provider.locations as Array<Location | string>)
        .filter((loc): loc is Location => typeof loc === 'object' && loc !== null)
        .map((loc) => loc.name || loc.address?.city)
        .filter(Boolean)
        .slice(0, 2)
        .join(', ')
    : '';

  // Root-level address fallback for unclaimed/seeded providers with no locations
  const addressLine = !locationNames && provider.address
    ? [provider.address.street, provider.address.city, provider.address.state].filter(Boolean).join(', ')
    : '';

  // Handle booking or navigate to provider page
  const handleBooking = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card onClick from firing
    if (onBookClick) {
      onBookClick(provider);
    } else {
      const identifier = provider.username || provider._id;
      navigate(`/provider/${identifier}`);
    }
  };

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl sm:rounded-2xl shadow-md ${scaleOnHover ? 'hover:shadow-xl' : ''} transition-all duration-300 overflow-hidden cursor-pointer transform border border-gray-100 flex flex-col h-full ${scaleOnHover ? 'hover:scale-[1.02]' : ''}`}
    >
      {/* Provider Image */}
      <div className="relative h-36 xs:h-40 sm:h-48 bg-gradient-to-br from-primary-100 to-accent-100">
        <SafeImage
          src={provider.bannerImage || provider.logo || provider.profileImage}
          fallbackSrc={defaultProviderImage}
          alt={provider.name}
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
          width={400}
          height={192}
          {...(eagerLoad ? { loading: 'eager' as const, fetchPriority: 'high' as const } : {})}
        />
        
        {/* Status Badge */}
        {(() => {
          const statusInfo = getProviderStatusInfo(provider.status);
          return (
            <div className={`absolute top-2 left-2 sm:top-3 sm:left-3 flex items-center gap-1 px-2 py-1 sm:px-2.5 sm:py-1 rounded-full shadow-lg ${statusInfo.bgColor} ${statusInfo.textColor}`}>
              <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${statusInfo.dotColor} ${statusInfo.animate ? 'animate-pulse' : ''}`} />
              <span className="text-[10px] sm:text-xs font-semibold">{statusInfo.label}</span>
            </div>
          );
        })()}

        {/* Rating Badge */}
        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex items-center space-x-1 bg-white/95 backdrop-blur-sm px-2 py-1 sm:px-3 sm:py-1.5 rounded-full shadow-lg">
          <StarIcon className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
          <span className="font-semibold text-xs sm:text-sm text-gray-900">{Number(provider.rating).toFixed(1)}</span>
          <span className="text-gray-500 text-[10px] sm:text-sm">({provider.totalReviews || 0})</span>
        </div>

        {/* Unclaimed Badge */}
        {isUnclaimed && (
          <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 flex items-center gap-1 px-2 py-1 sm:px-2.5 sm:py-1 rounded-full bg-gray-500/80 backdrop-blur-sm shadow-lg">
            <ExclamationCircleIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
            <span className="text-[10px] sm:text-xs font-medium text-white">Unclaimed Profile</span>
          </div>
        )}
      </div>

      {/* Provider Info */}
      <div className="p-3 xs:p-4 sm:p-5 flex flex-col flex-1">
        {/* Name & Verified Badge */}
        <div className="flex items-start justify-between mb-1.5 sm:mb-2">
          <h3 className="text-base xs:text-lg sm:text-xl font-bold text-gray-900 flex-1 mr-2 line-clamp-1">
            {provider.name}
          </h3>
          {!isUnclaimed && (
            <CheckBadgeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500 flex-shrink-0" />
          )}
        </div>

        {/* Specialization */}
        <p className="text-primary-600 font-medium text-sm sm:text-base mb-2 sm:mb-3 line-clamp-1">{provider.specialization}</p>

        {/* Experience */}
        <div className="flex items-center text-gray-600 mb-2 sm:mb-3">
          <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
          <span className="text-xs sm:text-sm">
            {provider.experience} {provider.experience === 1 ? 'year' : 'years'} experience
          </span>
        </div>

        {/* Distance or Locations */}
        {provider.distanceFormatted ? (
          <div className="flex items-center text-primary-600 mb-3 sm:mb-4">
            <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 flex-shrink-0" />
            <span className="text-xs sm:text-sm font-semibold">{provider.distanceFormatted}</span>
            {provider.nearestLocation?.name && (
              <span className="text-gray-500 text-xs sm:text-sm ml-1 truncate">
                · {provider.nearestLocation.name}
              </span>
            )}
            {provider.nearestLocation?.lat && provider.nearestLocation?.lng && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openDirections(
                    { latitude: provider.nearestLocation!.lat!, longitude: provider.nearestLocation!.lng! },
                    provider.nearestLocation?.name
                  );
                }}
                className="ml-2 p-2.5 -m-1.5 hover:bg-primary-100 rounded-full transition-colors"
                title="Get Directions"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
              </button>
            )}
          </div>
        ) : (locationNames || addressLine) ? (
          <div className="flex items-center text-gray-600 mb-3 sm:mb-4">
            <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 flex-shrink-0" />
            <span className="text-xs sm:text-sm truncate">{locationNames || addressLine}</span>
          </div>
        ) : null}

        {/* Qualifications (falls back to Specialties if none) — horizontally scrollable */}
        {(() => {
          const items = (provider.qualifications && provider.qualifications.length > 0)
            ? provider.qualifications
            : (provider.specialties && provider.specialties.length > 0)
            ? provider.specialties
            : null;
          if (!items) return null;
          return (
            <div className="flex flex-nowrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 overflow-x-auto scrollbar-hide">
              {items.slice(0, 2).map((item, index) => (
                <span
                  key={index}
                  className="flex-shrink-0 text-[10px] xs:text-xs px-2 py-0.5 sm:px-3 sm:py-1 bg-primary-50 text-primary-700 rounded-full font-medium"
                >
                  {item}
                </span>
              ))}
              {items.length > 2 && (
                <span className="text-[10px] flex-shrink-0 xs:text-xs px-2 py-0.5 sm:px-3 sm:py-1 bg-gray-100 text-gray-600 rounded-full font-medium">
                  +{items.length - 2} more
                </span>
              )}
            </div>
          );
        })()}

        {/* Bio (truncated) */}
        {provider.bio && (
          <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">{provider.bio}</p>
        )}

        {/* Spacer to push button to bottom */}
        <div className="flex-1" />

        {/* Action Button */}
        {isUnclaimed ? (
          <button 
            onClick={(e) => {
              e.stopPropagation();
              if (onClaimClick) {
                onClaimClick(provider);
              } else {
                const identifier = provider.username || provider._id;
                navigate(`/provider/${identifier}?claim=true`);
              }
            }}
            className="w-full py-2.5 sm:py-3 bg-gray-600 text-white text-sm sm:text-base font-semibold rounded-lg sm:rounded-xl hover:bg-gray-700 hover:shadow-lg transition-all duration-300 transform hover:scale-105 mt-auto"
          >
            Claim Profile
          </button>
        ) : (
          <button 
            onClick={handleBooking}
            className="w-full py-2.5 sm:py-3 bg-gradient-to-r from-primary-600 to-accent-600 text-white text-sm sm:text-base font-semibold rounded-lg sm:rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105 mt-auto"
          >
            Book Appointment
          </button>
        )}
      </div>
    </div>
  );
};

export default ProviderCard;
